<template>
  <div class="commissioning-detail pa-10">
    <title-page>{{ pageTitle }}</title-page>

    <v-alert v-if="error" type="error" dense outlined class="mb-4">{{ error }}</v-alert>
    <v-skeleton-loader v-if="loading && !order" type="card, card" />

    <template v-else-if="order">
      <!-- Où en est-elle, et ce qu'on attend -->
      <v-card class="mb-4">
        <v-card-text>
          <div class="d-flex align-center flex-wrap mb-4" style="gap: 8px">
            <v-icon>{{ kindIcon(order.kind) }}</v-icon>
            <span class="text-h6 font-weight-regular">{{ beneficiaryOf(order) }}</span>
            <router-link target="_blank" :to="{ name: 'organizations.detail', params: { id: order.organizationId } }" class="text-body-2">{{ order.organizationName }}</router-link>
            <v-chip small :color="statusColor(order.status)" dark>{{ statusLabel(order.status) }}</v-chip>
            <v-chip v-if="order.installation" small outlined>{{ $t(`onboarding.commissioning.installation.${order.installation}`) }}</v-chip>
            <!-- Ce qui est posé ou activé : un clic mène à sa fiche -->
            <order-equipment-links :order="order" class="text-body-2 ml-2" />
            <v-spacer />
            <!-- La file où elle attend, et depuis quand : un clic y ramène -->
            <v-chip v-if="order.step && !['done', 'closed'].includes(order.step)" small outlined color="primary" :to="{ name: 'onboarding.commissionings', query: { queue: order.step, order: order.id } }">
              <v-icon left small>mdi-inbox-full-outline</v-icon>
              {{ $t('onboarding.queue.in-queue', { queue: $t(`onboarding.queue.step.${order.step}`), date: $options.filters.formatDate(order.stepSince) }) }}
            </v-chip>
          </div>
          <ol class="commissioning-detail__steps">
            <li v-for="step in steps" :key="step.key" :class="step.class">
              <div class="font-weight-medium">{{ $t(`onboarding.commissioning.flow.${step.key}`) }}</div>
              <div class="text-caption text--secondary">{{ step.detail }}</div>
            </li>
          </ol>

          <order-field-issue :order="order" class="mt-4 mb-0" />
          <order-plan-failure v-if="order.step === 'commissioning'" :order="order" :resumable="isInstallable(order) && canActivate" class="mt-4 mb-0" />

          <v-alert v-if="['cancelled', 'rejected'].includes(order.status)" type="warning" dense text class="mt-4 mb-0">
            {{ order.refusedByPersonAt
              ? $t('onboarding.commissioning.refused-by-person', { reason: closingReasonLabel(order.closingReason), date: $options.filters.formatDate(order.refusedByPersonAt) })
              : $t(`onboarding.commissioning.closed-because.${order.status}`, { reason: closingReasonLabel(order.closingReason) }) }}
          </v-alert>
          <v-alert v-else color="amber lighten-5" dense class="mt-4 mb-0 black--text">
            <div class="d-flex align-center flex-wrap" style="gap: 8px">
              <div><b>{{ $t('onboarding.commissioning.expected-action') }} :</b> {{ expectedActionOf(order) }}</div>
              <v-spacer />
              <order-approval-actions v-if="order.status === 'pending-approval' && canManage" :order="order" @updated="apply" />
              <order-reminder-action v-else-if="order.step === 'person' && mayFollowOrders" :order="order" @updated="apply" />
              <!-- Un badge : la logistique le prépare et le poste ; le gestionnaire de flotte le remet -->
              <v-btn v-else-if="order.step === 'shipment' && canManage" small color="primary" :to="{ name: 'onboarding.badge-shipments.preparation', params: { id: order.id } }">
                <v-icon left small>mdi-package-variant</v-icon>{{ $t('onboarding.badge-shipments.prepare') }}
              </v-btn>
              <v-btn v-else-if="order.step === 'handover'" small color="primary" @click="designation = 'handover'">
                <v-icon left small>mdi-card-account-details-outline</v-icon>{{ $t('onboarding.pending-orders.hand-over') }}
              </v-btn>
            </div>
          </v-alert>
        </v-card-text>
      </v-card>

      <v-row>
        <v-col cols="12" lg="7">
          <!-- Ce qui sera fait : figé à la création -->
          <v-card class="mb-4">
            <v-card-title class="text-subtitle-1">{{ $t('onboarding.commissioning.what-will-be-done') }}</v-card-title>
            <v-card-text>
              <v-simple-table dense class="commissioning-detail__facts">
                <tbody>
                  <tr><th>{{ $t('onboarding.commissioning.kind-label') }}</th><td>{{ kindLabel(order.kind) }}</td></tr>
                  <tr><th>{{ $t('onboarding.commissioning.holder-label') }}</th><td>{{ $t(`onboarding.commissioning.holder.${order.holder}`) }}</td></tr>
                  <tr v-if="order.holder === 'member'">
                    <th>{{ $t('onboarding.commissioning.person') }}</th>
                    <td>
                      <router-link target="_blank" v-if="order.memberId" :to="{ name: 'member', params: { memberId: order.memberId } }">{{ personName(order) }}</router-link>
                      <span v-else>{{ personName(order) }}</span>
                      <div v-if="order.person && order.person.email" class="text-caption">{{ order.person.email }}</div>
                      <div v-if="order.person && order.person.phone" class="text-caption">{{ order.person.phone }}</div>
                    </td>
                  </tr>
                  <tr v-if="isInstallable(order) || order.locationId || order.newLocation">
                    <th>{{ $t('onboarding.commissioning.location') }}</th>
                    <td>
                      <router-link v-if="placedLocation" target="_blank" :to="{ name: 'location.detail', params: { id: order.locationId } }">{{ locationLabel }}</router-link>
                      <span v-else>{{ locationLabel }}</span>
                    </td>
                  </tr>
                  <tr>
                    <th>{{ $t('onboarding.commissioning.equipment-label') }}</th>
                    <td>
                      <order-equipment-links v-if="!isVehicleOrder" :order="order" :fallback="equipmentLabel" class="text--primary" />
                      <span v-else>{{ equipmentLabel }}</span>
                    </td>
                  </tr>
                  <tr v-if="order.deviceType">
                    <th>{{ $te(`onboarding.commissioning.form.device-type.${order.kind}`) ? $t(`onboarding.commissioning.form.device-type.${order.kind}`) : $t('onboarding.commissioning.equipment-label') }}</th>
                    <td>{{ $te(`onboarding.device-type.${order.deviceType}`) ? $t(`onboarding.device-type.${order.deviceType}`) : order.deviceType }}</td>
                  </tr>
                  <tr v-if="isInstallable(order)"><th>{{ $t('onboarding.commissioning.restrictions') }}</th><td>{{ restrictionsLabel }}</td></tr>
                  <tr v-if="order.vehicleId">
                    <th>{{ $t('onboarding.commissioning.vehicle-label') }}</th>
                    <td><order-equipment-links :order="order" /></td>
                  </tr>
                  <tr v-if="order.connectionType">
                    <th>{{ $t('onboarding.commissioning.connection-type') }}</th>
                    <td>{{ $t(`onboarding.configuration.licence.connection.${order.connectionType}`) }}</td>
                  </tr>
                  <tr v-if="order.profile">
                    <th>{{ $t('onboarding.commissioning.profile') }}</th>
                    <td>
                      <router-link target="_blank" :to="{ name: 'organizations.equipment-profiles', params: { id: order.organizationId } }">{{ order.profile.name }}</router-link>
                      <v-chip x-small outlined color="primary" class="ml-1">{{ $t(`onboarding.equipment-profile.usage.${order.profile.usage}`) }}</v-chip>
                    </td>
                  </tr>
                  <tr v-if="order.licence">
                    <th>{{ $t('onboarding.commissioning.licence') }}</th>
                    <td>
                      {{ licenceLabel }}
                      <!-- La licence enregistrée vit sur la borne : on la retrouve dans son onglet -->
                      <div v-if="order.kind === 'charging-point' && order.equipmentId">
                        <router-link target="_blank" :to="{ name: 'charging-points.detail.licences', params: { id: order.equipmentId } }">{{ $t('onboarding.commissioning.see-licences') }}</router-link>
                      </div>
                    </td>
                  </tr>
                  <tr v-if="order.ownership && order.kind === 'charging-point'">
                    <th>{{ $t('onboarding.commissioning.ownership') }}</th><td>{{ $t(`onboarding.commissioning.ownership-value.${order.ownership}`) }}</td>
                  </tr>
                  <tr v-if="order.depositAmount"><th>{{ $t('onboarding.commissioning.deposit') }}</th><td>{{ money(order.depositAmount) }}</td></tr>
                  <tr v-if="order.dataUse"><th>{{ $t('onboarding.commissioning.data-use') }}</th><td>{{ $t(`onboarding.commissioning.data-use-value.${order.dataUse}`) }}</td></tr>
                  <!-- Les options des badges, figées à la création de la commande. -->
                  <tr v-if="badgeOptions">
                    <th>{{ $t('onboarding.commissioning.badges') }}</th>
                    <td>
                      {{ $t(`onboarding.commissioning.badge-supply.${badgeOptions.supply}`) }}
                      · {{ $t(badgeOptions.letter ? 'onboarding.commissioning.badge-letter-yes' : 'onboarding.commissioning.badge-letter-no') }}
                      · {{ $t(`onboarding.commissioning.badge-roaming.${badgeOptions.roaming}`) }}
                      <!-- Le courrier imprimé reste gardé : on le rouvre (et le réimprime) à tout moment, même la commande finie -->
                      <div v-if="badgeOptions.letter" class="mt-1">
                        <v-btn x-small outlined color="primary" :loading="openingLetter" @click="openLetter">
                          <v-icon left x-small>mdi-printer</v-icon>{{ $t('onboarding.commissioning.badge-letter-open') }}
                        </v-btn>
                      </div>
                    </td>
                  </tr>
                  <tr>
                    <th>{{ $t('onboarding.commissioning.origin-label') }}</th>
                    <td>
                      {{ $t(`onboarding.commissioning.origin.${order.origin}`) }}<span v-if="requesterName"> · {{ requesterName }}</span>
                      <div v-if="registrationId">
                        <router-link target="_blank" :to="{ name: 'onboarding.registrations.detail', params: { id: registrationId } }">{{ $t('onboarding.commissioning.see-registration') }}</router-link>
                      </div>
                    </td>
                  </tr>
                  <tr v-if="order.externalReference"><th>{{ $t('onboarding.commissioning.external-reference') }}</th><td>{{ order.externalReference }}</td></tr>
                  <tr v-if="order.desiredDate"><th>{{ $t('onboarding.commissioning.desired-date') }}</th><td>{{ dayOf(order.desiredDate) }}</td></tr>
                  <tr v-if="order.decidedAt"><th>{{ $t('onboarding.commissioning.decided-at') }}</th><td>{{ order.decidedAt | formatDate }}</td></tr>
                </tbody>
              </v-simple-table>
              <p class="text-caption text--secondary mt-3 mb-0">{{ $t('onboarding.commissioning.frozen') }}</p>
            </v-card-text>
          </v-card>
        </v-col>

        <v-col cols="12" lg="5">
          <!-- Ce qu'il faut réunir avant la mise en service, et qui le lève -->
          <v-card class="mb-4">
            <v-card-title class="text-subtitle-1">{{ $t('onboarding.commissioning.requirements') }}</v-card-title>
            <v-card-text>
              <order-requirements-list :order="order" />
            </v-card-text>
          </v-card>

          <!-- Qui pose, et quand -->
          <v-card v-if="isOpenOrder(order) || (order.survey && order.survey.outcome)" class="mb-4">
            <v-card-title class="text-subtitle-1">{{ $t('onboarding.commissioning.planning') }}</v-card-title>
            <v-card-text>
              <!-- La visite technique, quand la planification en prévoit une : comment, quand, ce qu'elle a conclu -->
              <order-survey-panel v-if="isInstallable(order) && (order.status === 'open' || order.survey)" :order="order" class="mb-4" @updated="apply" />

              <order-scheduling-panel v-if="isInstallable(order) && isOpenOrder(order)" :order="order" :technicians="technicians" @updated="apply">
                <template #actions>
                  <v-btn small text color="error" class="px-0" :disabled="working" @click="cancellation.open = true">{{ $t('onboarding.commissioning.cancel') }}</v-btn>
                </template>
              </order-scheduling-panel>
              <template v-else-if="isOpenOrder(order)">
                <p class="text-caption text--secondary">{{ $t('onboarding.commissioning.no-installation') }}</p>
                <v-btn small text color="error" class="px-0" :disabled="working" @click="cancellation.open = true">{{ $t('onboarding.commissioning.cancel') }}</v-btn>
              </template>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <!-- Poser : l'équipement, confirmé, puis le plan. Visible aussi une fois fait. -->
      <commissioning-activation
          v-if="isInstallable(order) && canActivate && (isOpenOrder(order) || order.plan)"
          :key="order.id + order.status"
          :order="order"
          @updated="load"
      />

      <!-- Sans pose (véhicule, badges) : la mise en service part seule quand tout est réuni ; on montre son plan. -->
      <v-card v-else-if="!isInstallable(order) && order.plan" class="mb-4">
        <v-card-title class="text-subtitle-1">
          {{ $t('onboarding.commissioning.commissioning-plan') }}
          <v-chip x-small class="ml-2" :color="order.plan.status === 'completed' ? 'green' : (order.plan.status === 'failed' ? 'red' : 'orange')" dark>
            {{ $t(`onboarding.registration.plan-status.${order.plan.status}`) }}
          </v-chip>
        </v-card-title>
        <v-card-text><plan-steps :steps="order.plan.steps" /></v-card-text>
      </v-card>
    </template>

    <v-dialog v-model="cancellation.open" max-width="520">
      <v-card>
        <v-card-title>{{ $t('onboarding.commissioning.cancel') }}</v-card-title>
        <v-card-text>
          <p class="text-caption">{{ $t('onboarding.commissioning.cancel-explained') }}</p>
          <v-textarea v-model="cancellation.reason" :label="$t('onboarding.commissioning.cancel-reason')" outlined rows="3" auto-grow />
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn small text @click="cancellation.open = false">{{ $t('close') }}</v-btn>
          <v-btn small color="error" :loading="working" :disabled="!cancellation.reason.trim()" @click="cancel">{{ $t('onboarding.commissioning.cancel') }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <badge-designation-dialog :value="!!designation" :order="order" @input="designation = $event ? designation : null" @updated="load" @done="load" />

  </div>
</template>

<script>
import dayjs from 'dayjs'
import CommissioningRepository from '@repository/CommissioningRepository'
import CommissioningPolicyRepository from '@repository/CommissioningPolicyRepository'
import CommissioningMixin, { VEHICLE_KINDS } from './CommissioningMixin'
import CommissioningActivation from './CommissioningActivation.vue'
import PlanSteps from './PlanSteps.vue'
import BadgeDesignationDialog from './Commissioning/BadgeDesignationDialog.vue'
import OrderApprovalActions from './Order/OrderApprovalActions.vue'
import OrderReminderAction from './Order/OrderReminderAction.vue'
import OrderFieldIssue from './Order/OrderFieldIssue.vue'
import OrderPlanFailure from './Order/OrderPlanFailure.vue'
import OrderRequirementsList from './Order/OrderRequirementsList.vue'
import OrderSurveyPanel from './Order/OrderSurveyPanel.vue'
import OrderSchedulingPanel from './Order/OrderSchedulingPanel.vue'
import OrderEquipmentLinks from './Order/OrderEquipmentLinks.vue'

/**
 * Une commande d'équipement : où elle en est et ce qu'on attend (la décision d'un gestionnaire,
 * les prérequis de la personne et du paiement, la pose), ce qui sera fait (figé à la création),
 * qui la pose et quand, et la mise en service elle-même depuis le back-office.
 */
export default {
  name: 'CommissioningDetail',

  components: {
    CommissioningActivation, PlanSteps, BadgeDesignationDialog, OrderApprovalActions, OrderReminderAction, OrderFieldIssue,
    OrderPlanFailure, OrderRequirementsList, OrderSurveyPanel, OrderSchedulingPanel, OrderEquipmentLinks,
  },

  mixins: [CommissioningMixin],

  props: {
    // Montée sous l'organisation (`organizations/:id/equipment-orders/:orderId`), l'id de la route est celui de
    // l'organisation : la commande arrive par cette prop. Seule, la fiche lit la sienne dans la route.
    orderId: { type: String, default: null },
  },

  data() {
    return {
      order: null,
      openingLetter: false,
      technicians: [],
      managerDataLoaded: false,
      subscriptions: [],
      vehicleFormulas: [],
      loading: false,
      working: false,
      error: null,
      designation: null,
      cancellation: { open: false, reason: '' },
    }
  },

  computed: {
    currentOrderId() {
      return this.orderId || this.$route.params.id
    },

    /** Un seul texte : le titre de page ne se compose pas d'éléments. */
    pageTitle() {
      return this.order ? `${this.$t('onboarding.commissioning.order')} · ${this.kindOf(this.order)}` : this.$t('onboarding.commissioning.order')
    },

    permissions() {
      return this.currentUser?.permissions || []
    },
    managerDataReady() {
      return this.canManage && !!this.order?.organizationId
    },

    canManage() {
      return this.permissions.includes('ONBOARDING_MODULE_MANAGE')
    },
    canActivate() {
      return this.canManage && this.permissions.includes('STATION_ADD')
    },

    /** La frise : commandée, décidée (quand une validation est en jeu), prérequis réunis, en service. */
    steps() {
      const o = this.order
      const pending = this.pendingRequirements(o)
      const keys = ['ordered']
      if (o.status === 'pending-approval' || o.decidedAt) keys.push('decided')
      keys.push('requirements', 'in-service')

      const reached = {
        ordered: true,
        decided: !!o.decidedAt,
        requirements: o.status === 'in-service' || (o.status === 'open' && pending.length === 0),
        'in-service': o.status === 'in-service',
      }
      const details = {
        ordered: dayjs(o.createdAt).format('DD/MM/YYYY') + (this.requesterName ? ` · ${this.requesterName}` : ''),
        decided: o.decidedAt ? dayjs(o.decidedAt).format('DD/MM/YYYY') : '',
        requirements: o.status === 'open' && pending.length ? pending.map(this.requirementLabel).join(', ') : '',
        'in-service': o.status === 'in-service' && o.closedAt ? dayjs(o.closedAt).format('DD/MM/YYYY') : '',
      }
      const current = keys.find(key => !reached[key])
      return keys.map(key => ({
        key,
        detail: details[key],
        class: reached[key] ? 'is-done' : (key === current && this.isOpenOrder(o) ? 'is-current' : 'is-next'),
      }))
    },

    /** Posée, la commande tient l'emplacement retenu (créé ou retrouvé) : c'est lui qu'on montre, avec sa fiche. */
    placedLocation() {
      return !!this.order.locationId && (this.order.status === 'in-service' || !this.order.newLocation)
    },

    isVehicleOrder() {
      return VEHICLE_KINDS.includes(this.order.kind)
    },

    locationLabel() {
      if (this.placedLocation) return this.order.locationName || this.addressOf({ ...this.order, newLocation: null })
      if (this.order.newLocation) return this.$t('onboarding.commissioning.location-new', { address: this.formatAddress(this.order.newLocation) })
      return this.addressOf(this.order)
    },

    equipmentLabel() {
      // Des badges : leurs numéros, jamais les identifiants des tokens qu'une ancienne commande gardait.
      if (this.order.kind === 'badge') {
        const numbers = Object.entries(this.order.designatedBadges || {}).filter(([, number]) => number)
        if (numbers.length) return numbers.map(([kind, number]) => `${this.$t(`onboarding.commissioning.badge-kind.${kind}`)} : ${number}`).join(' · ')
        const count = (this.order.preparedEquipmentId || '').split(',').filter(Boolean).length
        if (count) return this.$tc('onboarding.commissioning.badges-designated', count, { count })
      }
      if (this.order.equipmentId) return this.order.equipmentLabel || this.order.equipmentId
      if (this.order.preparedEquipmentId) return this.$t('onboarding.commissioning.equipment-prepared', { id: this.order.equipmentLabel || this.order.preparedEquipmentId })
      return this.$t(this.isInstallable(this.order) ? 'onboarding.commissioning.equipment-on-site' : 'onboarding.commissioning.equipment-by-person')
    },

    restrictionsLabel() {
      return this.restrictionsOf(this.order) || this.$t('onboarding.commissioning.no-restriction')
    },

    /** Le demandeur par son nom ; une adhésion ou un système n'en a pas. */
    requesterName() {
      return this.order?.requestedBy?.name || null
    },

    /** La commande née d'une adhésion la désigne : `registration:<id>`. */
    registrationId() {
      const reference = this.order?.originReference || ''
      return reference.startsWith('registration:') ? reference.slice('registration:'.length) : null
    },

    /** Les options d'une commande de badges, figées à sa création ({supply, letter, roaming}). */
    badgeOptions() {
      return this.order?.kind === 'badge' && this.order.options?.supply ? this.order.options : null
    },

    /** Abonnement · formule · options, par leur nom quand on peut les lire, sinon par leur identifiant. */
    licenceLabel() {
      const l = this.order.licence || {}
      const vehicleFormula = this.vehicleFormulas.find(f => f.subscriptionId === l.subscriptionId && f.formulaId === l.formulaId)
      if (vehicleFormula) return `${vehicleFormula.subscriptionName} · ${vehicleFormula.formulaName}`
      const subscription = this.subscriptions.find(s => s.id === l.subscriptionId)
      const nameIn = (list, id) => (list || []).find(item => item.id === id)?.name || id
      return [
        subscription ? subscription.name : l.subscriptionId,
        l.formulaId ? nameIn(subscription?.formulas, l.formulaId) : null,
        ...(l.optionIds || []).map(id => nameIn(subscription?.options, id)),
      ].filter(Boolean).join(' · ') || '-'
    },
  },

  watch: {
    // Une autre commande dans la même fiche (lien d'une commande à l'autre) : on la relit.
    async currentOrderId(id, previous) {
      if (!id || id === previous) return
      // Ses techniciens, abonnements et formules sont ceux de son organisation : relus avec elle.
      this.managerDataLoaded = false
      await this.load()
      if (this.managerDataReady) this.loadManagerData()
    },
    // L'utilisateur connecté peut arriver après la fiche (rechargement de page) : les données de
    // gestion se chargent dès que le droit et la commande sont connus, une fois.
    managerDataReady: {
      immediate: true,
      handler(ready) {
        if (ready) {
          this.loadManagerData()
        }
      },
    },
  },

  created() {
    this.load()
  },

  methods: {
    /** Le courrier tel qu'il a été imprimé, dans un nouvel onglet ; pas encore préparé, l'écran dit pourquoi. */
    async openLetter() {
      this.openingLetter = true
      try {
        window.open(await CommissioningRepository.badgeLetterFile(this.order.id), '_blank')
      } catch (e) {
        this.notifyError(e)
      } finally {
        this.openingLetter = false
      }
    },

    async loadManagerData() {
      if (this.managerDataLoaded) {
        return
      }
      this.managerDataLoaded = true
      const [technicians, subscriptions, vehicleFormulas] = await Promise.all([
        CommissioningRepository.technicians().catch(() => []),
        CommissioningPolicyRepository.licenceSubscriptions(this.order.organizationId).catch(() => []),
        VEHICLE_KINDS.includes(this.order.kind)
          ? CommissioningPolicyRepository.vehicleFormulas(this.order.organizationId).catch(() => [])
          : [],
      ])
      this.technicians = technicians
      this.subscriptions = subscriptions
      this.vehicleFormulas = vehicleFormulas
    },

    async load() {
      this.loading = true
      try {
        const order = await CommissioningRepository.get(this.currentOrderId)
        // Une autre commande : rien de l'ancienne ne se garde (apply fusionne).
        if (this.order && this.order.id !== order.id) this.order = null
        this.apply(order)
      } catch (e) {
        this.error = this.messageOf(e)
      } finally {
        this.loading = false
      }
    },

    /** La commande à jour (un geste, un panneau) : le plan, que seule la lecture complète porte, est gardé. */
    apply(order) {
      this.order = { ...this.order, ...order }
    },

    async run(action) {
      this.working = true
      this.error = null
      try {
        this.apply(await action())
        return true
      } catch (e) {
        this.error = this.messageOf(e)
        return false
      } finally {
        this.working = false
      }
    },

    money(amount, currency = 'EUR') {
      return new Intl.NumberFormat(this.$i18n.locale, { style: 'currency', currency: (currency || 'EUR').toUpperCase() }).format(amount)
    },

    async cancel() {
      if (await this.run(() => CommissioningRepository.cancel(this.order.id, this.cancellation.reason))) {
        this.cancellation = { open: false, reason: '' }
      }
    },
  },
}
</script>

<style scoped>
.commissioning-detail__steps {
  list-style: none;
  padding: 0;
  margin: 0;
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(140px, 1fr));
  gap: 8px;
}
.commissioning-detail__steps li {
  border-top: 4px solid #e0e0e0;
  padding-top: 8px;
}
.commissioning-detail__steps li.is-done {
  border-top-color: #2e7d32;
}
.commissioning-detail__steps li.is-current {
  border-top-color: #1565c0;
}
.commissioning-detail__steps li.is-next {
  color: #757575;
}
.commissioning-detail__facts th {
  width: 220px;
  color: #616161;
  font-weight: 400;
  text-align: left;
}
</style>
